/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import styled from "styled-components";
import { Box, theme } from "@nordcloud/gnui";
import { useClickOutside } from "~/tools";

type ContentData = {
  isVisible: boolean;
  alignRight: boolean;
};

type PopoverProps = {
  children?: React.ReactNode;
  trigger?: React.ReactNode;
  alignRight?: boolean;
  setIsPopover: React.Dispatch<React.SetStateAction<boolean>>;
  isPopover?: boolean;
};

const StyledPopover = styled.div`
  display: inline-block;
  position: relative;
`;

const Content = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  z-index: ${theme.zindex.dropdown};
  transition: visibility 0.1s ease-out, opacity 0.1s ease-out;
  margin-top: ${theme.spacing.spacing01};
  ${({ isVisible }: ContentData) =>
    isVisible &&
    `
    visibility: visible;
    opacity: 1;
  `}
  ${({ alignRight }: ContentData) =>
    alignRight &&
    `
      right: 0;
      &>div {
        text-align: right;
      }
    `}
`;

const StyledTrigger = styled.div`
  position: relative;
  cursor: pointer;
`;

export function Popover({
  children,
  trigger,
  alignRight,
  isPopover,
  setIsPopover,
}: PopoverProps) {
  const wrapper = React.useRef<HTMLDivElement>(null);

  useClickOutside(
    wrapper,
    () => {
      setIsPopover(false);
    },
    isPopover
  );

  return (
    <StyledPopover ref={wrapper}>
      <StyledTrigger
        className="Popover-trigger"
        onClick={() => setIsPopover(!isPopover)}
      >
        {trigger}
      </StyledTrigger>
      <Content
        isVisible={isPopover as boolean}
        alignRight={alignRight as boolean}
      >
        <Box innerSpacing="spacing03" shadow="shadow04">
          {children}
        </Box>
      </Content>
    </StyledPopover>
  );
}
